import React, { useEffect, useState} from "react";

const GREETINGS = [
  "Hello",
  "こんにちは",
  "Hola",
  "നമസ്കാരം",
  "Bonjour",
  "नमस्ते",
  "안녕하세요",
  "你好",
  "مرحبا",
];

const EXIT_DURATION = 1600;
const FONTS_FALLBACK_MS = 2000;

function RollingGreeting({ words, interval = 550, size = "clamp(1.4rem, 2vw, 3rem)" }) {
  const [index, setIndex] = useState(0);
  const [noTransition, setNoTransition] = useState(false);
  const displayWords = [...words, words[0]]; // clone first word at end for seamless wrap

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => i + 1);
    }, interval);

    return () => clearInterval(id);
  }, [interval]);

  useEffect(() => {
    if (index === words.length) {
      // landed on clone, wait for transition to finish then snap back silently
      const t = setTimeout(() => {
        setNoTransition(true);
        setIndex(0);
      }, 650);
      return () => clearTimeout(t);
    }

    if (noTransition) {
      // re-enable transition next tick after silent snap
      const t = requestAnimationFrame(() => setNoTransition(false));
      return () => cancelAnimationFrame(t);
    }
  }, [index, words.length, noTransition]);

  return (
    <div className="h-[1.3em] overflow-hidden" style={{ fontSize: size }}>
      <div
        className="flex flex-col items-center"
        style={{
          transform: `translateY(-${index * 1.3}em)`,
          transition: noTransition
            ? "none"
            : "transform 650ms cubic-bezier(.65,0,.35,1)",
        }}
      >
        {displayWords.map((word, i) => (
          <div
            key={i}
            className="flex h-[1.3em] items-center justify-center leading-none"
          >
            {word}
          </div>
        ))}
      </div>
    </div>
  );
}

function useLoadProgress(minDuration = 3000) {
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    let mounted = true;
    let finished = false;
    const startTime = Date.now();

    const finish = () => {
      if (finished) return;
      finished = true;
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDuration - elapsed);

      setTimeout(() => {
        if (mounted) setComplete(true);
      }, remaining);
    };

    const fontsReady = document.fonts?.ready;

    if (fontsReady) {
      fontsReady.then(finish).catch(finish);
      // fallback: fonts.ready hang risk → force finish after cap
      const fallback = setTimeout(finish, FONTS_FALLBACK_MS);
      return () => {
        mounted = false;
        clearTimeout(fallback);
      };
    } else {
      finish();
    }

    return () => {
      mounted = false;
    };
  }, [minDuration]);

  return complete;
}

export default function Preloader({
  onExitStart,
  onFinish,
  duration = 3000,
  greetingSize,
}) {
  const complete = useLoadProgress(duration);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (!complete || exiting) return;

    onExitStart?.();
    setExiting(true);

    const timer = setTimeout(() => {
      onFinish?.();
    }, EXIT_DURATION);

    return () => clearTimeout(timer);
  }, [complete, exiting, onExitStart, onFinish]);

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center overflow-hidden bg-white"
      style={{
        transform: exiting ? "translateY(-100vh)" : "translateY(0)",
        transition: `transform ${EXIT_DURATION}ms cubic-bezier(.76,0,.24,1)`,
        willChange: "transform",
      }}
    >
      <div
        className="relative z-[1] text-black"
        style={{ fontFamily: "inherit" }}
      >
        <RollingGreeting words={GREETINGS} size={greetingSize} />
      </div>
    </div>
  );
}